import { useFlag } from '@afterpaytouch/feature'
import { preferredPaymentDayService } from '@afterpaytouch/portal-api/consumer/preferred-payment-day'
import { Features, PreferredPaymentDayStatus, Weekday } from '../../model'
import {
  CheckoutAppSelector,
  useAppSelector,
  useConsumerTag,
  useCountry,
  useHasBillingAgreements,
  useIsBillingAgreementApprovalProcess,
  useIsConsumerLendingOrder,
  useIsExpress,
} from '..'
import { PreferredPaymentDayState } from './reducer'
import { createSelector } from '@reduxjs/toolkit'
import { CONSUMER_HAS_ALIGNED_PAYDAY_ONCE } from '@afterpaytouch/portal-api/consumer/preferred-payment-day/types'
import { useShowTermsDisclosure } from '../../component/Terms/helper'
import { Country } from '@afterpay/types'

/** api hooks */
export const {
  useGetPreferredPaymentDayQuery,
  useUpdatePreferredPaydayMutation,
  endpoints: { updatePreferredPayday },
} = preferredPaymentDayService

/** selectors */
export const preferredPaymentDaySelector: CheckoutAppSelector<PreferredPaymentDayState> = (state) => state.preferredPaymentDay

export const selectedPreferredDaySelector: CheckoutAppSelector<Weekday> = (state) => state.preferredPaymentDay.selectedPreferredDay

export const preferredPaymentDayStatusSelector: CheckoutAppSelector<PreferredPaymentDayStatus> = createSelector(
  preferredPaymentDaySelector,
  (state) => state.preferredPaymentDayStatus
)

/** hooks */
export const useSelectedPreferredDay = (): Weekday => useAppSelector(selectedPreferredDaySelector)
export const usePreferredPaymentDayStatus = (): PreferredPaymentDayStatus => useAppSelector(preferredPaymentDayStatusSelector)

export const useShowPreferredPaymentDay = (): boolean => {
  const tags = useConsumerTag()
  const country = useCountry()
  // ANZ: only set/show preferred payment day if the consumer has no align tag;
  // US: always set preferred payment day, so skip tag check
  const hasNoAlignTag =
    country === Country.US || typeof tags[CONSUMER_HAS_ALIGNED_PAYDAY_ONCE] === 'undefined' || tags[CONSUMER_HAS_ALIGNED_PAYDAY_ONCE] === 'false'
  const isFeatureEnabled = useFlag(Features.ENABLE_ALIGN_PAYMENT_DAY)
  const { data, isSuccess } = useGetPreferredPaymentDayQuery(undefined, { skip: !hasNoAlignTag || !isFeatureEnabled })
  const isConsumerLendingOrder = useIsConsumerLendingOrder()
  const isBillingAgreement = useIsBillingAgreementApprovalProcess()
  const hasBillingAgreements = useHasBillingAgreements()
  const paymentDayStatus = usePreferredPaymentDayStatus()
  const isExpress = useIsExpress()
  const showTermsDisclosure = useShowTermsDisclosure()

  const pristineCondition = isSuccess && data?.preferredPaymentDay === null && paymentDayStatus === PreferredPaymentDayStatus.Pristine
  const manuallySetCondition = paymentDayStatus === PreferredPaymentDayStatus.ManuallySet

  const eligibleConditions: boolean[] = [
    isFeatureEnabled,
    hasNoAlignTag,
    !isConsumerLendingOrder,
    !(isBillingAgreement || hasBillingAgreements),
    !isExpress,
    pristineCondition || manuallySetCondition,
    !showTermsDisclosure,
  ]

  return eligibleConditions.every(Boolean)
}

export const useIsUpdatePreferredPaymentDay = (): boolean => {
  const showPreferredPaymentDay = useShowPreferredPaymentDay()
  const preferredPaymentDayStatus = usePreferredPaymentDayStatus()
  const country = useCountry()

  // US consumers never have their preferred payment day set on their behalf
  return showPreferredPaymentDay && preferredPaymentDayStatus === PreferredPaymentDayStatus.Pristine && country !== Country.US
}

export const useIsHidePaymentDay = (): boolean => {
  const showPreferredPaymentDay = useShowPreferredPaymentDay()
  const country = useCountry()

  return country === Country.US || !showPreferredPaymentDay
}
